import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import clsx from 'clsx';
import { dataTemplate, PERF_TEMPLATES, type PerfScript, rampRateOf, rampSeconds, shapeStages, templateShape, withRampRates, type LoadShape, type RampRate, type PerfConfig, type PerfTemplate, type Scenario, type Slo, type ThinkTime } from '@1qa/shared';
import { CircleAlert, Plus, Trash2 } from '@/components/icons';
import { api, type LoadScriptRow } from '@/lib/api';
import type { DataWhenUsedUp, PerfDataSetView } from '@1qa/shared';
import { DataFileDialog } from './DataFiles';
import { evenShares, formatMinutes, formatNumber, loadPreview, nextScenarioId, planTransactions, rampPace, scenarioPeaks, simpleGoals, withSimpleGoals, withSplit } from '@/lib/perf';
import { Button, Card, CardBody, CardHeader, Field, Input, Select } from '@/components/ui';
import { TimeChart } from './TimeChart';

const TEMPLATE_INFO: Record<PerfTemplate, { label: string; text: string }> = {
  smoke: { label: 'Smoke', text: 'A few users for a minute: does it work at all?' },
  load: { label: 'Load', text: 'Ramp up to the expected users, hold, ramp down.' },
  stress: { label: 'Stress', text: 'A staircase up to twice the users, to find where goals break.' },
  spike: { label: 'Spike', text: 'Normal load, then eight times as many users within seconds.' },
  soak: { label: 'Soak', text: 'Expected users for hours: leaks and slow decline.' },
};
const SERIES = ['text-[var(--series-1)]', 'text-[var(--series-2)]', 'text-[var(--series-3)]', 'text-[var(--series-4)]', 'text-[var(--series-5)]'];
const GOAL_METRICS: Slo['metric'][] = ['p50', 'p90', 'p95', 'p99', 'errorRate'];

/** Minutes in the form, whole seconds in the plan. */
function MinutesField({ label, hint, seconds, onChange, disabled }: { label: string; hint?: string; seconds: number; onChange: (s: number) => void; disabled?: boolean }) {
  return (
    <Field label={label} hint={hint}>
      {(id) => <Input id={id} type="number" min={0} step={0.5} disabled={disabled} value={Math.round((seconds / 60) * 100) / 100} onChange={(e) => onChange(Math.max(0, Math.round(Number(e.target.value) * 60)))} />}
    </Field>
  );
}

/** How long a ramp takes, in words. */
const rampTime = (s: number) => (s < 120 ? `${s} s` : formatMinutes(s));

/** A ramp as the plan stores it: a rate (the time follows from the users) or a time in seconds (0: all at once). */
export interface Ramp {
  rate?: RampRate;
  seconds: number;
}

/**
 * Users mode: how users start (or stop) - N per second / minute, over a number of minutes (1QA works out the users per
 * second), or all at once - for `total` users.
 */
export function RampRateField({ label, verb, total, seconds, rate, onChange, disabled }: { label: string; verb: string; total: number; seconds: number; rate?: RampRate; onChange: (r: Ramp) => void; disabled?: boolean }) {
  const kind = rate ? rate.per : seconds > 0 ? 'time' : 'all';
  const hint = rate ? `${total} users ${verb} in ${rampTime(rampSeconds(total, rate))}` : seconds > 0 ? `${rampPace(total, seconds)} (${total} users in ${rampTime(seconds)})` : `All ${total} users ${verb} at once`;
  const choose = (v: string) => {
    if (v === 'all') return onChange({ seconds: 0 });
    if (v === 'time') return onChange({ seconds: seconds || 60 });
    const users = rate?.users ?? rampRateOf(total, seconds || 60)?.users ?? Math.max(1, Math.ceil(total / 10));
    const next = { users: v === 'm' && !rate && seconds ? Math.max(1, Math.round((total / seconds) * 60)) : users, per: v as RampRate['per'] };
    onChange({ rate: next, seconds: rampSeconds(total, next) });
  };
  return (
    <Field label={label} hint={hint}>
      {(id) => (
        <div className="flex gap-2">
          {rate ? (
            <Input
              id={id}
              type="number"
              min={1}
              disabled={disabled}
              aria-label={`${label}: users`}
              value={rate.users}
              onChange={(e) => {
                const next = { ...rate, users: Math.max(1, Math.round(Number(e.target.value) || 1)) };
                onChange({ rate: next, seconds: rampSeconds(total, next) });
              }}
            />
          ) : seconds > 0 ? (
            <Input id={id} type="number" min={0} step={0.5} disabled={disabled} aria-label={`${label}: minutes`} value={Math.round((seconds / 60) * 100) / 100} onChange={(e) => onChange({ seconds: Math.max(0, Math.round(Number(e.target.value) * 60)) })} />
          ) : null}
          <Select
            id={kind === 'all' ? id : undefined}
            aria-label={`${label}: how`}
            disabled={disabled}
            value={kind}
            onChange={(e) => choose(e.target.value)}
            options={[
              { value: 's', label: 'users per second' },
              { value: 'm', label: 'users per minute' },
              { value: 'time', label: 'minutes' },
              { value: 'all', label: 'all at once' },
            ]}
          />
        </div>
      )}
    </Field>
  );
}

/** A scenario's test data: the project's, or a data file (one row per journey) and what happens when its rows are used. */
function ScenarioData({ scenario, sets, script, readOnly, onChange, onUpload }: { scenario: Scenario; sets: PerfDataSetView[]; script?: PerfScript; readOnly?: boolean; onChange: (patch: Partial<Scenario>) => void; onUpload: () => void }) {
  const set = sets.find((d) => d.id === scenario.dataSetId);
  const names = script ? dataTemplate(script).columns : [];
  const filled = set ? names.filter((n) => set.columns.includes(n)) : [];
  const fromProject = set ? names.filter((n) => !set.columns.includes(n)) : names;
  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-[1.4fr_1fr]">
      <Field
        label="Test data"
        hint={
          set
            ? `Each journey takes the next of its ${formatNumber(set.rowCount)} rows${filled.length ? `: ${filled.join(', ')}` : ''}.${fromProject.length ? ` ${fromProject.join(', ')} from the project’s test data.` : ''}`
            : scenario.dataSetId
              ? 'This data file was deleted: choose another one.'
              : 'The same values for every journey. A data file gives each journey its own row (for example one user per row).'
        }
      >
        {(id) => (
          <Select
            id={id}
            disabled={readOnly}
            value={scenario.dataSetId ?? ''}
            onChange={(e) => (e.target.value === '__upload' ? onUpload() : onChange({ dataSetId: e.target.value || undefined, whenUsedUp: e.target.value ? (scenario.whenUsedUp ?? 'loop') : undefined }))}
            options={[
              { value: '', label: 'Project test data' },
              ...(scenario.dataSetId && !set ? [{ value: scenario.dataSetId, label: 'Deleted data file' }] : []),
              ...sets.map((d) => ({ value: d.id, label: `${d.name} (${formatNumber(d.rowCount)} rows)` })),
              ...(readOnly ? [] : [{ value: '__upload', label: 'Upload a CSV or Excel file…' }]),
            ]}
          />
        )}
      </Field>
      {scenario.dataSetId && (
        <Field label="When every row is used" hint={scenario.whenUsedUp === 'stop' ? `Each row is used once${set ? `: ${formatNumber(set.rowCount)} journeys in all` : ''}; then this scenario’s users stop sending.` : 'Start again at row 1 until the test ends.'}>
          {(id) => (
            <Select
              id={id}
              disabled={readOnly}
              value={scenario.whenUsedUp ?? 'loop'}
              onChange={(e) => onChange({ whenUsedUp: e.target.value as DataWhenUsedUp })}
              options={[
                { value: 'loop', label: 'Start again at row 1' },
                { value: 'stop', label: 'Stop sending' },
              ]}
            />
          )}
        </Field>
      )}
    </div>
  );
}

const SPLITS: { value: PerfConfig['split']; label: string }[] = [
  { value: 'even', label: 'Equally' },
  { value: 'percent', label: 'By percentage' },
  { value: 'users', label: 'By number of users' },
];

/**
 * A performance test's plan: scenarios (scripts of the project's library with their share of the load, start delay,
 * think time, pacing), the load strategy (template, users or arrival rate, ramp-up / steady / ramp-down or own
 * stages) with a preview of every scenario's load over time, and goals.
 */
export function LoadPlanEditor({
  projectId,
  plan,
  onChange,
  scripts,
  transactionsOf,
  readOnly,
}: {
  projectId: string;
  plan: PerfConfig;
  onChange: (plan: PerfConfig) => void;
  scripts: LoadScriptRow[];
  /** The scripts the plan uses (transactions for goals, test data names for data files), by script id. */
  transactionsOf: Map<string, PerfScript>;
  readOnly?: boolean;
}) {
  const dataSets = useQuery({ queryKey: ['perf-data-sets', projectId], queryFn: () => api.perfDataSets(projectId) });
  // The scenario whose "Upload a file…" is open.
  const [uploadFor, setUploadFor] = useState<number | null>(null);
  const load = plan.load;
  // Equal and per-user splits keep the shares (and the maximum users) in step with the scenarios.
  const change = (next: PerfConfig) => onChange(withSplit(next));
  // Ramp rates (users mode) decide the ramp times, also when the total changes. Users per scenario need the plain shape.
  const setLoad = (patch: Partial<LoadShape>) => {
    const next = withRampRates({ ...load, ...patch });
    change({ ...plan, load: next, split: plan.split === 'users' && (next.mode !== 'users' || next.stages) ? 'percent' : plan.split });
  };
  const setRamp = (which: 'up' | 'down', r: Ramp) => setLoad(which === 'up' ? { rampUpRate: r.rate, rampUpS: r.seconds } : { rampDownRate: r.rate, rampDownS: r.seconds });
  const setScenario = (i: number, patch: Partial<Scenario>) => change({ ...plan, scenarios: plan.scenarios.map((sc, k) => (k === i ? { ...sc, ...patch } : sc)) });
  const setSplit = (split: PerfConfig['split']) => {
    // By number of users starts from the users each scenario has now.
    const peaks = scenarioPeaks(plan);
    change({ ...plan, split, scenarios: plan.scenarios.map((sc, i) => (split === 'users' ? { ...sc, users: Math.max(1, peaks[i]!) } : sc)) });
  };
  const usersSplit = load.mode === 'users' && !load.stages;
  const peaks = load.mode === 'users' ? scenarioPeaks(plan) : null;
  const shareSum = plan.scenarios.reduce((n, sc) => n + sc.share, 0);
  const sharesOk = Math.abs(shareSum - 100) <= 0.5;
  const preview = useMemo(() => (plan.scenarios.length && sharesOk ? loadPreview(plan) : null), [plan, sharesOk]);
  const goals = simpleGoals(plan.slos);
  const extraGoals = plan.slos.filter((s) => s.scope !== 'all');
  const txNames = planTransactions(plan, transactionsOf);
  const unit = load.mode === 'users' ? 'users' : `new users per ${load.timeUnit === 'm' ? 'minute' : 'second'}`;

  const addScenario = () => {
    const used = new Set(plan.scenarios.map((sc) => sc.scriptId));
    const script = scripts.find((s) => !used.has(s.id)) ?? scripts[0];
    if (!script) return;
    const added = { id: nextScenarioId(plan.scenarios.map((sc) => sc.id)), name: script.name.slice(0, 80), scriptId: script.id, share: 0, startAfterS: 0, thinkTime: 'recorded' as ThinkTime };
    if (plan.split === 'users') return change({ ...plan, scenarios: [...plan.scenarios, { ...added, users: Math.max(1, Math.round(load.target / plan.scenarios.length)) }] });
    const next = [...plan.scenarios, added];
    const shares = evenShares(next.length);
    change({ ...plan, scenarios: next.map((sc, i) => ({ ...sc, share: shares[i]! })) });
  };
  const removeScenario = (i: number) => {
    const next = plan.scenarios.filter((_, k) => k !== i);
    // Drop goals of the removed scenario's transactions, which no longer exist.
    change({ ...plan, scenarios: next, slos: plan.slos.filter((s) => s.scope === 'all' || !s.scope.startsWith(`${plan.scenarios[i]!.name} › `)) });
  };
  const applyTemplate = (t: PerfTemplate) => change({ ...plan, template: t, split: plan.split === 'users' ? 'percent' : plan.split, load: { ...templateShape(t, load.mode === 'users' ? Math.max(1, load.stages ? Math.max(...load.stages.map((s) => s.target)) : load.target) : load.target, load.mode), timeUnit: load.timeUnit, ...(load.maxVus ? { maxVus: load.maxVus } : {}) } });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Scenarios"
          description="Each scenario is a script from this project’s library with its part of the users. Users of different scenarios run at the same time."
          actions={
            plan.scenarios.length > 0 && (
              <Field label="Split the users">
                {(id) => (
                  <Select
                    id={id}
                    disabled={readOnly}
                    value={plan.split}
                    onChange={(e) => setSplit(e.target.value as PerfConfig['split'])}
                    options={SPLITS.filter((o) => o.value !== 'users' || usersSplit || plan.split === 'users')}
                  />
                )}
              </Field>
            )
          }
        />
        <CardBody className="space-y-4">
          {scripts.length === 0 ? (
            <p className="text-sm text-fg-secondary">
              This project has no scripts yet.{' '}
              <Link to={`/projects/${projectId}/perf/scripts/new`} state={{ record: true }} className="text-link hover:underline">
                Make a script
              </Link>{' '}
              with the 1QA Recorder or from a HAR file first.
            </p>
          ) : (
            <>
              <ul className="space-y-3">
                {plan.scenarios.map((sc, i) => (
                  <li key={sc.id} className="rounded-card p-4 ring-1 ring-border-subtle ring-inset" data-testid="scenario">
                    <div className="grid gap-3 md:grid-cols-[1.2fr_1.4fr_0.6fr_0.7fr_auto] md:items-end">
                      <Field label="Scenario">{(id) => <Input id={id} value={sc.name} maxLength={80} disabled={readOnly} onChange={(e) => setScenario(i, { name: e.target.value })} />}</Field>
                      <Field label="Script">
                        {(id) => (
                          <Select
                            id={id}
                            disabled={readOnly}
                            value={sc.scriptId}
                            onChange={(e) => setScenario(i, { scriptId: e.target.value })}
                            options={[...(scripts.some((s) => s.id === sc.scriptId) ? [] : [{ value: sc.scriptId, label: 'Deleted script' }]), ...scripts.map((s) => ({ value: s.id, label: `${s.name} (${s.requests} requests)` }))]}
                          />
                        )}
                      </Field>
                      {plan.split === 'users' ? (
                        <Field label="Users" hint={`${formatNumber(sc.share)} % of ${formatNumber(load.target)}`}>
                          {(id) => <Input id={id} type="number" min={1} step={1} disabled={readOnly} value={sc.users ?? 1} onChange={(e) => setScenario(i, { users: Math.max(1, Math.round(Number(e.target.value) || 1)) })} />}
                        </Field>
                      ) : (
                        <Field label="Share (%)" hint={peaks ? `${formatNumber(peaks[i] ?? 0)} users` : undefined}>
                          {(id) => <Input id={id} type="number" min={0.1} max={100} step={1} disabled={readOnly || plan.split === 'even'} value={sc.share} onChange={(e) => setScenario(i, { share: Number(e.target.value) })} />}
                        </Field>
                      )}
                      <MinutesField label="Starts after (min)" seconds={sc.startAfterS} disabled={readOnly} onChange={(s) => setScenario(i, { startAfterS: s })} />
                      {!readOnly && plan.scenarios.length > 1 ? (
                        <Button variant="ghost" aria-label={`Remove scenario ${sc.name}`} onClick={() => removeScenario(i)} icon={<Trash2 className="size-4" />} />
                      ) : (
                        <span />
                      )}
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_0.6fr_0.6fr_0.8fr]">
                      <Field label="Pause between pages">
                        {(id) => (
                          <Select
                            id={id}
                            disabled={readOnly}
                            value={typeof sc.thinkTime === 'string' ? sc.thinkTime : 'custom'}
                            onChange={(e) => setScenario(i, { thinkTime: e.target.value === 'custom' ? { minS: 1, maxS: 3 } : (e.target.value as 'recorded' | 'none') })}
                            options={[
                              { value: 'recorded', label: 'As in the script' },
                              { value: 'none', label: 'None (as fast as possible)' },
                              { value: 'custom', label: 'Between … seconds' },
                            ]}
                          />
                        )}
                      </Field>
                      {typeof sc.thinkTime === 'object' ? (
                        <>
                          <Field label="Min (s)">{(id) => <Input id={id} type="number" min={0} step={0.5} disabled={readOnly} value={(sc.thinkTime as { minS: number }).minS} onChange={(e) => setScenario(i, { thinkTime: { ...(sc.thinkTime as { minS: number; maxS: number }), minS: Number(e.target.value) } })} />}</Field>
                          <Field label="Max (s)">{(id) => <Input id={id} type="number" min={0} step={0.5} disabled={readOnly} value={(sc.thinkTime as { maxS: number }).maxS} onChange={(e) => setScenario(i, { thinkTime: { ...(sc.thinkTime as { minS: number; maxS: number }), maxS: Number(e.target.value) } })} />}</Field>
                        </>
                      ) : (
                        <span className="hidden sm:col-span-2 sm:block" />
                      )}
                      {load.mode === 'users' && (
                        <Field label="Pacing (s)" hint="A user starts its next journey at most this often">
                          {(id) => <Input id={id} type="number" min={0} step={1} placeholder="none" disabled={readOnly} value={sc.pacingS ?? ''} onChange={(e) => setScenario(i, { pacingS: e.target.value ? Number(e.target.value) : undefined })} />}
                        </Field>
                      )}
                    </div>
                    <ScenarioData
                      scenario={sc}
                      sets={dataSets.data ?? []}
                      script={transactionsOf.get(sc.scriptId)}
                      readOnly={readOnly}
                      onChange={(patch) => setScenario(i, patch)}
                      onUpload={() => setUploadFor(i)}
                    />
                  </li>
                ))}
              </ul>
              {uploadFor !== null && plan.scenarios[uploadFor] && (
                <DataFileDialog
                  projectId={projectId}
                  open
                  onClose={() => setUploadFor(null)}
                  expected={(() => {
                    const sc = transactionsOf.get(plan.scenarios[uploadFor]!.scriptId);
                    return sc ? dataTemplate(sc).columns : [];
                  })()}
                  onSaved={(set) => setScenario(uploadFor, { dataSetId: set.id, whenUsedUp: plan.scenarios[uploadFor]!.whenUsedUp ?? 'loop' })}
                />
              )}
              <div className="flex flex-wrap items-center gap-3">
                {!readOnly && (
                  <>
                    <Button variant="outline" size="sm" onClick={addScenario} icon={<Plus className="size-4" />}>
                      Add scenario
                    </Button>
                    {plan.scenarios.length > 1 && plan.split === 'percent' && (
                      <Button variant="ghost" size="sm" onClick={() => change({ ...plan, scenarios: plan.scenarios.map((sc, i) => ({ ...sc, share: evenShares(plan.scenarios.length)[i]! })) })}>
                        Split evenly
                      </Button>
                    )}
                  </>
                )}
                <span role="status" className={clsx('ml-auto text-sm', sharesOk ? 'text-fg-secondary' : 'flex items-center gap-1.5 text-warning')}>
                  {!sharesOk && <CircleAlert className="size-4" aria-hidden />}
                  {plan.split === 'users'
                    ? `${formatNumber(load.target)} users in total`
                    : `Shares add up to ${Math.round(shareSum * 10) / 10} %${sharesOk ? '' : ' (they must add up to 100 %)'}${sharesOk && peaks ? ` · ${formatNumber(peaks.reduce((n, p) => n + p, 0))} users` : ''}`}
                </span>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Load strategy" description="How the users start, keep going and stop. Every scenario gets its part of the users, as split above." />
        <CardBody className="space-y-5">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Template">
            {PERF_TEMPLATES.map((t) => (
              <button
                key={t}
                type="button"
                disabled={readOnly}
                aria-pressed={plan.template === t}
                title={TEMPLATE_INFO[t].text}
                onClick={() => applyTemplate(t)}
                className={clsx('rounded-full px-4 py-1.5 text-xs font-semibold tracking-label uppercase ring-1 ring-inset disabled:opacity-60', plan.template === t ? 'bg-primary text-primary-fg ring-primary' : 'text-fg ring-border hover:ring-primary')}
              >
                {TEMPLATE_INFO[t].label}
              </button>
            ))}
            <span className="self-center text-xs text-fg-muted">{TEMPLATE_INFO[plan.template].text}</span>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <Field label="Load as">
              {(id) => (
                <Select
                  id={id}
                  disabled={readOnly}
                  value={load.mode}
                  onChange={(e) => setLoad({ mode: e.target.value as LoadShape['mode'] })}
                  options={[
                    { value: 'users', label: 'Users at the same time' },
                    { value: 'rate', label: 'New users per second / minute' },
                  ]}
                />
              )}
            </Field>
            {!load.stages && (
              <Field
                label={load.mode === 'users' ? 'Maximum users (VUs)' : 'New users'}
                hint={load.mode === 'rate' ? 'Each starts one journey' : plan.split === 'users' ? 'The scenarios’ users added up' : 'All running at the same time once started'}
              >
                {(id) => <Input id={id} type="number" min={1} disabled={readOnly || plan.split === 'users'} value={load.target} onChange={(e) => setLoad({ target: Math.max(1, Math.round(Number(e.target.value))) })} />}
              </Field>
            )}
            {load.mode === 'rate' && (
              <Field label="Per">
                {(id) => (
                  <Select
                    id={id}
                    disabled={readOnly}
                    value={load.timeUnit}
                    onChange={(e) => setLoad({ timeUnit: e.target.value as 's' | 'm' })}
                    options={[
                      { value: 's', label: 'second' },
                      { value: 'm', label: 'minute' },
                    ]}
                  />
                )}
              </Field>
            )}
            {load.mode === 'rate' && (
              <Field label="Most users at once" hint="Empty: 1QA works it out">
                {(id) => <Input id={id} type="number" min={1} placeholder="automatic" disabled={readOnly} value={load.maxVus ?? ''} onChange={(e) => setLoad({ maxVus: e.target.value ? Math.max(1, Number(e.target.value)) : undefined })} />}
              </Field>
            )}
          </div>

          {!load.stages && load.mode === 'users' ? (
            <div className="grid gap-4 sm:grid-cols-3">
              <RampRateField label="Ramp-up" verb="start" total={load.target} seconds={load.rampUpS} rate={load.rampUpRate} disabled={readOnly} onChange={(r) => setRamp('up', r)} />
              <MinutesField label="Steady (min)" hint="All users repeat their scripts; goals are judged here" seconds={load.steadyS} disabled={readOnly} onChange={(s) => setLoad({ steadyS: s })} />
              <RampRateField label="Ramp-down" verb="stop" total={load.target} seconds={load.rampDownS} rate={load.rampDownRate} disabled={readOnly} onChange={(r) => setRamp('down', r)} />
            </div>
          ) : !load.stages ? (
            <div className="grid gap-4 sm:grid-cols-3">
              <MinutesField label="Ramp-up (min)" hint="From 0 to the peak" seconds={load.rampUpS} disabled={readOnly} onChange={(s) => setLoad({ rampUpS: s })} />
              <MinutesField label="Steady (min)" hint="Goals are judged here" seconds={load.steadyS} disabled={readOnly} onChange={(s) => setLoad({ steadyS: s })} />
              <MinutesField label="Ramp-down (min)" hint="Back to 0" seconds={load.rampDownS} disabled={readOnly} onChange={(s) => setLoad({ rampDownS: s })} />
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-fg-secondary">Own stages: each one moves the load to its target over its time.</p>
              <ol className="space-y-2">
                {load.stages.map((st, i) => (
                  <li key={i} className="grid grid-cols-[auto_1fr_1fr_auto] items-end gap-3">
                    <span className="pb-3 text-xs text-fg-muted tabular-nums">{i + 1}</span>
                    <MinutesField
                      label="Minutes"
                      seconds={st.durationS}
                      disabled={readOnly}
                      onChange={(s) => setLoad({ stages: load.stages!.map((x, k) => (k === i ? { ...x, durationS: Math.max(1, s) } : x)) })}
                    />
                    <Field label={load.mode === 'users' ? 'To users' : 'To new users'}>
                      {(id) => <Input id={id} type="number" min={0} disabled={readOnly} value={st.target} onChange={(e) => setLoad({ stages: load.stages!.map((x, k) => (k === i ? { ...x, target: Math.max(0, Math.round(Number(e.target.value))) } : x)), target: Math.max(1, ...load.stages!.map((x, k) => (k === i ? Number(e.target.value) : x.target))) })} />}
                    </Field>
                    {!readOnly && load.stages!.length > 1 ? <Button variant="ghost" aria-label={`Remove stage ${i + 1}`} onClick={() => setLoad({ stages: load.stages!.filter((_, k) => k !== i) })} icon={<Trash2 className="size-4" />} /> : <span />}
                  </li>
                ))}
              </ol>
              {!readOnly && (
                <Button variant="outline" size="sm" onClick={() => setLoad({ stages: [...load.stages!, { durationS: 60, target: load.stages![load.stages!.length - 1]?.target ?? load.target }] })} icon={<Plus className="size-4" />}>
                  Add stage
                </Button>
              )}
            </div>
          )}
          {!readOnly && (
            <button
              type="button"
              className="text-sm text-link hover:underline"
              onClick={() => (load.stages ? setLoad({ stages: undefined, ...templateShape('load', load.target, load.mode), timeUnit: load.timeUnit }) : setLoad({ stages: shapeStages(load).length ? shapeStages(load) : [{ durationS: 60, target: load.target }] }))}
            >
              {load.stages ? 'Use ramp-up, steady and ramp-down instead' : 'Use own stages instead (staircase, spikes…)'}
            </button>
          )}

          {preview ? (
            <div className="viz-root space-y-2">
              <TimeChart
                title={load.mode === 'users' ? 'Users over time' : 'New users per second over time'}
                stepSeconds={preview.stepS}
                format={(v) => formatNumber(load.mode === 'users' ? Math.round(v) : Math.round(v * 10) / 10)}
                series={[
                  ...preview.series.map((s, i) => ({ label: s.name, color: SERIES[i % SERIES.length]!, values: s.values })),
                  ...(preview.series.length > 1 ? [{ label: 'All', color: 'text-fg-muted', values: preview.total, dashed: true }] : []),
                ]}
              />
              <p className="text-sm text-fg-secondary" data-testid="plan-summary">
                {formatMinutes(preview.durationS)} in total · {load.mode === 'users' ? `up to ${formatNumber(preview.peak.vus)} users` : `up to ${formatNumber(preview.peak.rps ?? 0)} new users per second, at most ${formatNumber(preview.peak.vus)} at once`}
              </p>
            </div>
          ) : (
            <p className="text-sm text-fg-muted">The preview appears when the scenarios’ shares add up to 100 %.</p>
          )}
          <p className="sr-only">Load: {unit}</p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Goals" description="Judged on the steady part of the run. A missed goal fails the test." />
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="95 % of requests faster than (ms)">
              {(id) => <Input id={id} type="number" min={1} placeholder="no goal" disabled={readOnly} value={goals.p95Ms ?? ''} onChange={(e) => onChange({ ...plan, slos: withSimpleGoals(plan.slos, { ...goals, p95Ms: e.target.value ? Number(e.target.value) : null }) })} />}
            </Field>
            <Field label="Errors under (%)">
              {(id) => <Input id={id} type="number" min={0} step={0.1} placeholder="no goal" disabled={readOnly} value={goals.errorPct ?? ''} onChange={(e) => onChange({ ...plan, slos: withSimpleGoals(plan.slos, { ...goals, errorPct: e.target.value ? Number(e.target.value) : null }) })} />}
            </Field>
          </div>
          {extraGoals.length > 0 && (
            <ul className="space-y-2">
              {extraGoals.map((g) => {
                const at = plan.slos.indexOf(g);
                const set = (patch: Partial<Slo>) => onChange({ ...plan, slos: plan.slos.map((s, k) => (k === at ? { ...s, ...patch } : s)) });
                return (
                  <li key={at} className="grid items-end gap-3 sm:grid-cols-[0.7fr_1.6fr_0.8fr_auto]">
                    <Field label="Measure">
                      {(id) => <Select id={id} disabled={readOnly} value={g.metric} onChange={(e) => set({ metric: e.target.value as Slo['metric'], max: e.target.value === 'errorRate' ? 0.01 : g.metric === 'errorRate' ? 1000 : g.max })} options={GOAL_METRICS.map((m) => ({ value: m, label: m === 'errorRate' ? 'errors' : m }))} />}
                    </Field>
                    <Field label="Transaction">
                      {(id) => <Select id={id} disabled={readOnly} value={g.scope} onChange={(e) => set({ scope: e.target.value })} options={[...(txNames.includes(g.scope) ? [] : [{ value: g.scope, label: `${g.scope} (not in this test)` }]), ...txNames.map((n) => ({ value: n, label: n }))]} />}
                    </Field>
                    <Field label={g.metric === 'errorRate' ? 'Under (%)' : 'Under (ms)'}>
                      {(id) => <Input id={id} type="number" min={0} disabled={readOnly} value={g.metric === 'errorRate' ? Math.round(g.max * 1000) / 10 : g.max} onChange={(e) => set({ max: g.metric === 'errorRate' ? Number(e.target.value) / 100 : Number(e.target.value) })} />}
                    </Field>
                    {!readOnly ? <Button variant="ghost" aria-label="Remove goal" onClick={() => onChange({ ...plan, slos: plan.slos.filter((_, k) => k !== at) })} icon={<Trash2 className="size-4" />} /> : <span />}
                  </li>
                );
              })}
            </ul>
          )}
          {!readOnly && txNames.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => onChange({ ...plan, slos: [...plan.slos, { metric: 'p95', scope: txNames[0]!, max: 1000 }] })} icon={<Plus className="size-4" />}>
              Goal for one transaction
            </Button>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
